<template>
  <section id="home">
    <div v-if="showBanner && activeBanner" class="alert" @click="bannerClicked">
      <img :src="getBannerImageUrl(activeBanner.imageUrl)" :alt="activeBanner.altText"/>
      <span class="close-btn" @click.stop="showBanner = false">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </span>
    </div>
    <SpinnerLoading v-if="loading"/>
    <template v-else>
      <HeroGrid :products="heroProducts" :videos="tiktokVideos"/>      
      <div class="container-fluid pb-3">
        <div class="row">
          <div class="col-12 text-center">
            <h2 class="section-title">{{ $t('ourProducts') }}</h2>
            <p class="section-subtitle">{{ $t('ourProductsDesc') }}</p>
            <div class="section-cta d-flex justify-content-center mt-3">
              <router-link :to="{ name: 'Shop' }" class="btn-shop">
                {{ $t('header.viewAllProducts') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <div class="container-fluid py-2">
        <div class="row">
          <div class="col-12 col-md-6 mb-3">
            <ProductSlider :products="inflateHighlightedProducts" :brand="INFLATE" :autoplayState="true" :title="$t('news')"></ProductSlider>
          </div>
          <div class="col-12 col-md-6">
            <ProductSlider :products="activeHighlightedProducts" :brand="ACTIVE" :autoplayState="true" :title="$t('news')"></ProductSlider>
          </div>
        </div>
      </div>
      
      <div class="container-fluid py-2">
        <div class="row">
          <div class="col-12 col-md-6 mb-3">
            <ProductSlider :products="inflateStockProducts" :brand="INFLATE" :autoplayState="false" :title="$t('stock')"></ProductSlider>
          </div>
          <div class="col-12 col-md-6">
            <ProductSlider :products="activeStockProducts" :brand="ACTIVE" :autoplayState="false" :title="$t('stock')"></ProductSlider>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import ProductSlider from "@/components/home/ProductSlider.vue";
import HeroGrid from "@/components/home/HeroGrid.vue";
import { mapState, mapActions } from "vuex";
import SpinnerLoading from "@/components/SpinnerLoading";
import { API_HELPER } from "@/helpers/api.js";
import { UTILS_HELPER } from "@/helpers/utils.js";
import { HomeResult } from "@/classes/HomeResult";

const ACTIVE = "ActiveBrands";
const INFLATE = "InflateYourImagination";
const ONLINESTORE = "Online Store";

export default {
  name: "Home",
  components: { HeroGrid, SpinnerLoading, ProductSlider },
   metaInfo () {
    return { 
      title: 'FactoryPlay',
      htmlAttrs: {
          lang: this.$i18n.locale,
      },
      meta: 
      [
        {name: 'description', content: UTILS_HELPER.htmlToText(this.$t("intro"))},
        {name: 'keywords', content: this.$t("vueRouter.defaultKeywords")},
        // Twitter Card
        {name: 'twitter:card', content: "summary"},
        {name: 'twitter:title', content: 'FactoryPlay'},
        {name: 'twitter:description', content: UTILS_HELPER.htmlToText(this.$t("intro"))},
        {name: 'twitter:image', content: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, "assets/logo.png"]) },
        //Facebook OpenGraph
        {property: 'og:title', content: 'FactoryPlay'},
        {property: 'og:site_name', content: 'FactoryPlay'},
        {property: 'og:type', content: 'website'},
        {property: 'og:image', content: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, "assets/logo.png"]) },
        {property: 'og:description', content: UTILS_HELPER.htmlToText(this.$t("intro"))}
      ], 
      link: [
        {rel: 'alternate', hreflang: 'pt', href: process.env.VUE_APP_URL_ROOT},
        {rel: 'alternate', hreflang: 'en', href: process.env.VUE_APP_URL_ROOT},
        {rel: 'alternate', hreflang: 'es', href: process.env.VUE_APP_URL_ROOT},
      ]
    } 
  }, 
  data() {
    return {
      ACTIVE: ACTIVE,
      INFLATE: INFLATE,
      ONLINESTORE: ONLINESTORE,
      showBanner: true
    }
  },
  mounted: function()
  {
    // GET list of products
    this.getAllProducts(HomeResult.ActiveInStock);
    this.getAllProducts(HomeResult.InflateInStock);
    this.getAllProducts(HomeResult.ActiveHighlighted);
    this.getAllProducts(HomeResult.InflateHighlighted);

    // GET TikTok videos from key values service
    this.getTikTokVideos();

    // GET active banner
    this.getActiveBanner();

    // GET orderId from backoffice
    const loggedIn = localStorage.getItem('user');
    if(this.$route.query.id && loggedIn)
    {
      this.getOrderDetail(this.$route.query.id);
    }
    if(this.$route.query.id && !loggedIn){
      this.$router.push({name: 'Login'});  
    }
  },
  computed:
  {
    ...mapState({
      groupedListOutput: state => state.products.groupedListOutput,
      orderOutput: state => state.orders.orderOutput,
      tiktokVideosOutput: state => state.keyValues.tiktokVideosOutput,
      activeBanner: state => state.banners.bannerOutput,
    }),
    loading: function()
    {
      return this.heroProducts.length == 0 || this.activeHighlightedProducts.length == 0 || this.inflateHighlightedProducts.length == 0 || this.activeStockProducts.length == 0 || this.inflateStockProducts.length == 0;
    },
    tiktokVideos: function()
    {
      // Return the videos from the API, or fallback to default videos if not loaded yet
      if (this.tiktokVideosOutput && this.tiktokVideosOutput.length > 0) {
        return this.tiktokVideosOutput;
      }
      
      // Fallback videos in case API fails or is not available
      return [
        'https://www.tiktok.com/@factoryplay_inflatables/video/7556288980992527639',
        'https://www.tiktok.com/@factoryplay_inflatables/video/7354013497824447776',
        'https://www.tiktok.com/@factoryplay_inflatables/video/7546175557642358049',
        'https://www.tiktok.com/@factoryplay_inflatables/video/7545955167112367393'
      ];
    },
    heroProducts: function()
    {
      var result = [];

      if(this.groupedListOutput[HomeResult.InflateHighlighted] && this.groupedListOutput[HomeResult.InflateHighlighted].length) result.push(this.groupedListOutput[HomeResult.InflateHighlighted][0]);
      if(this.groupedListOutput[HomeResult.ActiveHighlighted] && this.groupedListOutput[HomeResult.ActiveHighlighted].length) result.push(this.groupedListOutput[HomeResult.ActiveHighlighted][0]);
      
      return result;
    },
    activeHighlightedProducts: function()
    {
      var result = this.groupedListOutput[HomeResult.ActiveHighlighted] ?? [];
      const length = result.length;

      if(length)
      {
        result = result.slice(1);
      }
      
      return result;
    },
    inflateHighlightedProducts: function()
    {
      var result = this.groupedListOutput[HomeResult.InflateHighlighted] ?? [];
      const length = result.length;

      if(length)
      {
        result = result.slice(1);
      }
      
      return result;
    },
    activeStockProducts: function()
    {
      return this.groupedListOutput[HomeResult.ActiveInStock] ?? [];
    },
    inflateStockProducts: function()
    {
      return this.groupedListOutput[HomeResult.InflateInStock] ?? [];
    },
  },
  methods: {
    ...mapActions({
        getAllProducts: 'products/getAll',
        getOrderDetail: 'orders/getDetail',
        getTikTokVideos: 'keyValues/getTikTokVideos',
        getActiveBanner: 'banners/getActiveBanner'
    }),
    navigation (id) {
      this.$router.push({name: 'StaticPages', query: {id: id} });
    },
    getBannerImageUrl(path) {
      if (path && path.startsWith('http')) return path;
      return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, path]);
    },
    bannerClicked() {
      if (!this.activeBanner || !this.activeBanner.link) return;
      const link = this.activeBanner.link;
      if (link.startsWith('http')) {
        window.open(link, '_blank');
      } else if (!isNaN(link)) {
        this.navigation(parseInt(link));
      } else {
        this.$router.push(link);
      }
    },
    goToFacebook() {
      window.open('https://www.facebook.com/factoryplay.inflatables', '_blank');
    },
  },
  watch: 
  {
    orderOutput: function(val)
    {
      if(val && val.data && val.data.products)
      {
        const params = new URLSearchParams(window.location.search);
        if(params.has('index'))
        {
          const index = params.get('index');
          let productDetail = val.data.products[index];
          this.$router.push({name: 'Simulator', params: {slug: productDetail.slug, product: productDetail}});
        }
      }
    },
    '$i18n.locale': function() {
      // Reload TikTok videos when language changes
      this.getTikTokVideos();
    }
  }
}
</script>

<style scoped>
#home {
  position: relative;
}
::v-deep .box {
  width: 100%!important; 
}
.alert {
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
  height: 600px;
  z-index: 29;
  cursor: pointer;
}
.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.close-btn:hover {
  background-color: #f8f8f8;
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
}
.close-btn svg {
  width: 15px;
  height: 15px;
  stroke: black;
}
@media screen and (max-width: 991px) {
  .brands-container {
    flex-direction: column;
  }
  .alert{
    top: 0;
    right: 0;
    height: 300px;
  }
}
@media screen and (min-width: 1200px) {
  ::v-deep .img-footer{
    background-image: url("../assets/footer/footer-img-home.png") !important;
  }
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #17224d;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.btn-shop{
  display: inline-block;
  background-color: #17224d;
  color: #fff;
  border-radius: 999px;
  padding: 10px 18px;
  font-weight: 600;
  text-decoration: none;
  transition: transform .15s ease, box-shadow .15s ease, background-color .2s ease;
  box-shadow: 0 6px 16px rgba(23,34,77,0.18);
}
.btn-shop:hover{
  color: #fff;
  background-color: #0f1940;
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(23,34,77,0.25);
}

@media screen and (max-width: 768px) {
  .section-title {
    font-size: 2rem;
  }
  
  .section-subtitle {
    font-size: 1rem;
    padding: 0 1rem;
  }
}
</style>